import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getLanguageDirection } from '../../i18n/config';
import LanguageSwitcherGuest from '../common/LanguageSwitcherGuest';
import LoadingScreen from '../common/LoadingScreen';
import OrganizationSelection from './OrganizationSelection';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2 } from 'lucide-react';
import { getStytchClient } from '@/config/stytch.config';
import { appConfig } from '@/config/app.config';
import { useAppDispatch } from '../../redux/store';
import { setDiscoveredOrganizations, setIntermediateSessionToken } from '../../redux/slices/organizationSlice';


const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [, setIsAutofilled] = useState(false);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Discovery state
  const [discoveredOrganizations, setDiscoveredOrganizationsState] = useState<any[]>([]);
  const [intermediateSessionToken, setIntermediateSessionTokenState] = useState<string | null>(null);
  const [showOrgSelection, setShowOrgSelection] = useState(false);

  const { loginWithMagicLink, authenticateWithStytch, switchOrganization, loading, error } = useAuth();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';

  const tokenProcessedRef = React.useRef(false);

  useEffect(() => {
    // Check for Stytch token in URL (from magic link or OAuth)
    const token = searchParams.get('token');
    const tokenType = searchParams.get('stytch_token_type');

    console.log('🔍 Login useEffect - URL params:', {
      hasToken: !!token,
      tokenType,
      tokenProcessed: tokenProcessedRef.current,
      allParams: Object.fromEntries(searchParams.entries())
    });

    if (token && !tokenProcessedRef.current) {
      const isDiscoveryToken = tokenType === 'discovery' || tokenType === 'discovery_oauth';
      const isMagicLinkToken = tokenType === 'multi_tenant_magic_links';

      if (isDiscoveryToken || isMagicLinkToken) {
        // Show loading screen immediately when processing token
        setIsProcessingOAuth(true);
        console.log('🚀 Starting token exchange...', { tokenType });
        tokenProcessedRef.current = true; // Mark as processed immediately

        // Exchange token for session token, then authenticate
        (async () => {
          try {
            console.log('📤 Sending token to backend...');
            // Exchange token for session token via backend
            const response = await fetch(`${appConfig.API_ENDPOINT}/auth/discovery/authenticate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                discovery_token: token,
                token_type: tokenType
              })
            });

            console.log('📥 Backend response status:', response.status);

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error('❌ Backend error:', errorData);
              throw new Error(errorData.error || t('errors.auth.failedToExchangeToken', 'Failed to exchange token'));
            }

            const data = await response.json();
            console.log('✅ Token exchanged successfully:', {
              hasSessionToken: !!data.session_token,
              hasIntermediateToken: !!data.intermediate_session_token,
              organizations: data.discovered_organizations?.length || 0,
              emailAddress: data.email_address
            });

            // Check if backend auto-created an organization and returned a session token
            if (data.session_token) {
              console.log('🎉 Session token received - authenticating directly');
              await authenticateWithStytch(data.session_token);
              return;
            }

            // Store discovery data
            if (data.discovered_organizations && data.discovered_organizations.length > 0) {
              console.log('🏢 Login: Received discovered organizations:', data.discovered_organizations);
              setDiscoveredOrganizationsState(data.discovered_organizations);
              setIntermediateSessionTokenState(data.intermediate_session_token);

              // Persist to Redux and LocalStorage
              console.log('📦 Dispatching discovery data to Redux');
              dispatch(setDiscoveredOrganizations(data.discovered_organizations));
              dispatch(setIntermediateSessionToken(data.intermediate_session_token));
              localStorage.setItem('discovered_organizations', JSON.stringify(data.discovered_organizations));
              localStorage.setItem('intermediate_session_token', data.intermediate_session_token);

              // Auto-select if only one organization
              if (data.discovered_organizations.length === 1) {
                console.log('🏢 Auto-selecting single organization');
                // Backend returns flat structure: { organization_id, organization_name, ... }
                const orgId = data.discovered_organizations[0].organization_id;
                await switchOrganization(orgId, data.intermediate_session_token);
              } else {
                // Show organization selection screen
                console.log('🏢 Multiple organizations found, showing selection screen');
                setShowOrgSelection(true);
                setIsProcessingOAuth(false);
              }
            } else {
              console.error('❌ No organizations found and no session token');
              throw new Error(t('errors.auth.noOrganizationsFound', 'No organizations found for this user'));
            }
          } catch (err) {
            console.error('❌ Authentication error:', err);
            setFormError(t('auth.magicLink.failed'));
            setIsProcessingOAuth(false);
            tokenProcessedRef.current = false;
          }
        })();
      }
    }

    const timer = setTimeout(() => {
      const emailInput = document.getElementById('email-magic') as HTMLInputElement;
      if (emailInput && emailInput.value) {
        setIsAutofilled(true);
        setEmail(emailInput.value);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchParams, t]); // Removed authenticateWithStytch from dependencies

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setMagicLinkSent(false);

    if (!email) {
      setFormError(t('errors.validation.emailRequired'));
      return;
    }

    try {
      await loginWithMagicLink(email);
      setMagicLinkSent(true);
    } catch {
      // Error is handled by the auth context
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      setFormError('');

      // Use Stytch SDK to start Google OAuth
      const stytch = getStytchClient();

      // Start Google OAuth discovery flow
      await stytch.oauth.google.discovery.start({
        discovery_redirect_url: `${appConfig.FRONTEND_URL}/login`
      });

      // The SDK will automatically redirect to Google
    } catch (error) {
      console.error('Google login error:', error);
      setFormError(t('auth.google.initiateFailed'));
      setIsGoogleLoading(false);
    }
  };

  const handleSelectOrganization = async (organizationId: string, intermediateToken: string) => {
    try {
      await switchOrganization(organizationId, intermediateToken);
      // switchOrganization will handle navigation
    } catch (error) {
      console.error('Failed to select organization:', error);
      setFormError(t('auth.organization.joinFailed'));
      setShowOrgSelection(false);
    }
  };

  // Show organization selection screen if user has multiple orgs
  if (showOrgSelection && discoveredOrganizations.length > 0 && intermediateSessionToken) {
    return (
      <OrganizationSelection
        discoveredOrganizations={discoveredOrganizations}
        intermediateSessionToken={intermediateSessionToken}
        onSelectOrganization={handleSelectOrganization}
      />
    );
  }

  // Show loading screen when processing OAuth callback
  if (isProcessingOAuth) {
    return <LoadingScreen />;
  }

  return (
    <div className="container max-w-md mx-auto px-4" dir={getLanguageDirection(i18n.language)}>
      {/* Language Switcher */}
      <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} mb-4 mt-4`}>
        <LanguageSwitcherGuest variant="compact" showLabel={false} />
      </div>

      <Card className="mt-8 shadow-lg">
        {/* Logo */}
        <div className="flex justify-center pt-8 pb-4">
          <img
            src="/OmniLawLogoTransparent.png"
            alt={t('common.labels.omnilaw')}
            className="h-32 w-auto object-contain"
          />
        </div>

        <CardHeader className="space-y-1 pt-4">
          <CardTitle className="text-2xl font-bold text-center">
            {t('pages.login.title')}
          </CardTitle>
        </CardHeader>

        <CardContent>
          {(error || formError) && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {formError || error}
              </AlertDescription>
            </Alert>
          )}

          {magicLinkSent && (
            <Alert className="mb-4">
              <AlertDescription>
                {t('auth.magicLink.sentMessage')}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleMagicLinkSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-magic">
                    {t('pages.login.emailLabel')}
                  </Label>
                  <Input
                    id="email-magic"
                    type="email"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    placeholder={t('auth.placeholders.email')}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || magicLinkSent}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.auth.loading')}
                    </>
                  ) : magicLinkSent ? (
                    t('auth.magicLink.sent')
                  ) : (
                    t('auth.magicLink.send')
                  )}
                </Button>

                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      {t('auth.divider.orContinueWith')}
                    </span>
                  </div>
                </div>

                {/* Google OAuth Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleLogin}
                  disabled={loading || isGoogleLoading}
                >
                  {isGoogleLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.auth.loading')}
                    </>
                  ) : (
                    <>
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      {t('auth.google.continueWith')}
                    </>
                  )}
                </Button>

                <div className="text-center text-sm">
                  <RouterLink
                    to="/signup"
                    className="text-gold hover:underline font-medium"
                  >
                    {t('pages.login.signUpLink')}
                  </RouterLink>
                </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
